import { CheckCircle2, Star } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type AbandonmentReason,
  type FeatureUsed,
  type ImprovementPriority,
  type UsageFrequency,
  type WouldRecommend,
  getLatestProductFeedbackSurvey,
  submitProductFeedbackSurvey,
} from '../../../services/productFeedbackService';

const USAGE_OPTIONS: UsageFrequency[] = [
  'active_daily',
  'active_weekly',
  'occasional',
  'stopped',
  'tried_once',
];

const ABANDONMENT_OPTIONS: AbandonmentReason[] = [
  'no_results',
  'confusing_ui',
  'too_expensive',
  'technical_issues',
  'found_alternative',
  'not_job_searching',
  'forgot_about_it',
  'slow_performance',
  'privacy_concerns',
  'other',
];

const FEATURE_OPTIONS: FeatureUsed[] = [
  'resume_optimizer',
  'job_matching',
  'interview_prep',
  'apply_tracking',
  'ai_agent',
  'resume_builder',
  'job_board',
  'none',
];

const IMPROVEMENT_OPTIONS: ImprovementPriority[] = [
  'better_job_matches',
  'easier_resume_tools',
  'lower_pricing',
  'faster_performance',
  'better_mobile',
  'more_guidance',
  'other',
];

const RECOMMEND_OPTIONS: WouldRecommend[] = ['yes', 'maybe', 'no'];

function toggleInList<T extends string>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

const ProductFeedbackPage: React.FC = () => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [previousSubmittedAt, setPreviousSubmittedAt] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [usageFrequency, setUsageFrequency] = useState<UsageFrequency | ''>('');
  const [overallRating, setOverallRating] = useState(0);
  const [abandonmentReasons, setAbandonmentReasons] = useState<AbandonmentReason[]>([]);
  const [problemsFaced, setProblemsFaced] = useState('');
  const [featuresUsed, setFeaturesUsed] = useState<FeatureUsed[]>([]);
  const [improvementPriority, setImprovementPriority] = useState<ImprovementPriority | ''>('');
  const [wouldRecommend, setWouldRecommend] = useState<WouldRecommend | ''>('');
  const [additionalComments, setAdditionalComments] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const latest = await getLatestProductFeedbackSurvey();
        if (!cancelled && latest) setPreviousSubmittedAt(latest.created_at);
      } catch {
        /* non-blocking */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const validate = (): string | null => {
    if (!usageFrequency) return t('dashboard.productFeedback.errors.usageRequired');
    if (overallRating < 1) return t('dashboard.productFeedback.errors.ratingRequired');
    if (!improvementPriority) return t('dashboard.productFeedback.errors.improvementRequired');
    if (!wouldRecommend) return t('dashboard.productFeedback.errors.recommendRequired');
    return null;
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setSubmitting(true);
    setError(null);
    try {
      await submitProductFeedbackSurvey({
        usage_frequency: usageFrequency as UsageFrequency,
        overall_rating: overallRating,
        abandonment_reasons: abandonmentReasons,
        problems_faced: problemsFaced,
        features_used: featuresUsed,
        improvement_priority: improvementPriority as ImprovementPriority,
        would_recommend: wouldRecommend as WouldRecommend,
        additional_comments: additionalComments,
      });
      setSubmitted(true);
    } catch {
      setError(t('dashboard.productFeedback.errors.submitFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="ds-page">
        <div className="ds-card ds-card-sm max-w-3xl mx-auto p-8 text-center t-body">
          {t('dashboard.productFeedback.loading')}
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="ds-page">
        <div className="ds-card max-w-2xl mx-auto p-8 sm:p-10 text-center">
          <CheckCircle2 className="w-12 h-12 mx-auto mb-4 text-emerald-500" aria-hidden />
          <h1 className="t-section mb-2">{t('dashboard.productFeedback.thankYouTitle')}</h1>
          <p className="t-body text-[var(--ds-text-muted)]">
            {t('dashboard.productFeedback.thankYouBody')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="ds-page">
      <div className="max-w-3xl mx-auto space-y-6">
        <header className="space-y-2">
          <h1 className="t-section">{t('dashboard.productFeedback.title')}</h1>
          <p className="t-body text-[var(--ds-text-muted)]">{t('dashboard.productFeedback.subtitle')}</p>
          <p className="t-meta">{t('dashboard.productFeedback.privacyNote')}</p>
          {previousSubmittedAt ? (
            <p className="t-meta text-[var(--ds-text-muted)]">
              {t('dashboard.productFeedback.previousSubmission', {
                date: new Date(previousSubmittedAt).toLocaleDateString(),
              })}
            </p>
          ) : null}
        </header>

        <form className="ds-card p-6 sm:p-8 space-y-8" onSubmit={handleSubmit} noValidate>
          <fieldset className="space-y-3 border-0 p-0 m-0">
            <legend className="t-card-title mb-1">{t('dashboard.productFeedback.usageLabel')}</legend>
            <p className="t-meta mb-2">{t('dashboard.productFeedback.usageHelp')}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {USAGE_OPTIONS.map((option) => (
                <label key={option} className="flex items-start gap-2 min-h-[44px] cursor-pointer touch-manipulation">
                  <input
                    type="radio"
                    name="usage_frequency"
                    className="mt-1"
                    checked={usageFrequency === option}
                    onChange={() => setUsageFrequency(option)}
                  />
                  <span className="t-body">{t(`dashboard.productFeedback.usage.${option}`)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <fieldset className="space-y-3 border-0 p-0 m-0">
            <legend className="t-card-title mb-1">{t('dashboard.productFeedback.ratingLabel')}</legend>
            <div className="flex items-center gap-1">
              {[1, 2, 3, 4, 5].map((value) => (
                <button
                  key={value}
                  type="button"
                  className="min-h-[44px] min-w-[44px] flex items-center justify-center touch-manipulation"
                  aria-label={t('dashboard.productFeedback.ratingStar', { value })}
                  onClick={() => setOverallRating(value)}
                >
                  <Star
                    className={`w-7 h-7 ${value <= overallRating ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`}
                    aria-hidden
                  />
                </button>
              ))}
            </div>
          </fieldset>

          <fieldset className="space-y-3 border-0 p-0 m-0">
            <legend className="t-card-title mb-1">{t('dashboard.productFeedback.abandonmentLabel')}</legend>
            <p className="t-meta mb-2">{t('dashboard.productFeedback.abandonmentHelp')}</p>
            <div className="grid gap-2 sm:grid-cols-2">
              {ABANDONMENT_OPTIONS.map((option) => (
                <label key={option} className="flex items-start gap-2 min-h-[44px] cursor-pointer touch-manipulation">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={abandonmentReasons.includes(option)}
                    onChange={() => setAbandonmentReasons((prev) => toggleInList(prev, option))}
                  />
                  <span className="t-body">{t(`dashboard.productFeedback.abandonment.${option}`)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="space-y-2">
            <label className="t-card-title block" htmlFor="problems-faced">
              {t('dashboard.productFeedback.problemsLabel')}
            </label>
            <textarea
              id="problems-faced"
              className="ds-textarea w-full min-h-[120px]"
              placeholder={t('dashboard.productFeedback.problemsPlaceholder')}
              value={problemsFaced}
              onChange={(e) => setProblemsFaced(e.target.value)}
              maxLength={4000}
            />
          </div>

          <fieldset className="space-y-3 border-0 p-0 m-0">
            <legend className="t-card-title mb-1">{t('dashboard.productFeedback.featuresLabel')}</legend>
            <div className="grid gap-2 sm:grid-cols-2">
              {FEATURE_OPTIONS.map((option) => (
                <label key={option} className="flex items-start gap-2 min-h-[44px] cursor-pointer touch-manipulation">
                  <input
                    type="checkbox"
                    className="mt-1"
                    checked={featuresUsed.includes(option)}
                    onChange={() => setFeaturesUsed((prev) => toggleInList(prev, option))}
                  />
                  <span className="t-body">{t(`dashboard.productFeedback.features.${option}`)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="space-y-2">
            <label className="t-card-title block" htmlFor="improvement-priority">
              {t('dashboard.productFeedback.improvementLabel')}
            </label>
            <select
              id="improvement-priority"
              className="ds-select w-full min-h-[44px] touch-manipulation"
              value={improvementPriority}
              onChange={(e) => setImprovementPriority(e.target.value as ImprovementPriority)}
            >
              <option value="">{t('dashboard.productFeedback.improvementPlaceholder')}</option>
              {IMPROVEMENT_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {t(`dashboard.productFeedback.improvement.${option}`)}
                </option>
              ))}
            </select>
          </div>

          <fieldset className="space-y-3 border-0 p-0 m-0">
            <legend className="t-card-title mb-1">{t('dashboard.productFeedback.recommendLabel')}</legend>
            <div className="flex flex-wrap gap-3">
              {RECOMMEND_OPTIONS.map((option) => (
                <label key={option} className="flex items-center gap-2 min-h-[44px] cursor-pointer touch-manipulation">
                  <input
                    type="radio"
                    name="would_recommend"
                    checked={wouldRecommend === option}
                    onChange={() => setWouldRecommend(option)}
                  />
                  <span className="t-body">{t(`dashboard.productFeedback.recommend.${option}`)}</span>
                </label>
              ))}
            </div>
          </fieldset>

          <div className="space-y-2">
            <label className="t-card-title block" htmlFor="additional-comments">
              {t('dashboard.productFeedback.commentsLabel')}
            </label>
            <textarea
              id="additional-comments"
              className="ds-textarea w-full min-h-[100px]"
              placeholder={t('dashboard.productFeedback.commentsPlaceholder')}
              value={additionalComments}
              onChange={(e) => setAdditionalComments(e.target.value)}
              maxLength={4000}
            />
          </div>

          {error ? (
            <p className="t-meta text-red-600 dark:text-red-400" role="alert">{error}</p>
          ) : null}

          <button
            type="submit"
            className="ds-btn ds-btn-primary w-full sm:w-auto min-h-[44px] touch-manipulation"
            disabled={submitting}
          >
            {submitting ? t('dashboard.productFeedback.submitting') : t('dashboard.productFeedback.submit')}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProductFeedbackPage;
